import useServerPagination,{ServerPageStatus} from '../../components/ui/useServerPagination';
import StockReceiptFields from './StockReceiptFields';
import InwardPriceDefault from './InwardPriceDefault';
import HandoverOutwardActivity from './HandoverOutwardActivity';
import PermissionButton from "../../components/ui/PermissionButton";
import { UNIT_OPTIONS } from "../../utils/units";
import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import DepartmentTransferModal from './DepartmentTransferModal';
import { useAuth } from '../Context/AuthContext';

export default function MaterialLedger() {
  const { user, permissions = [] } = useAuth();
  const [showTransfer,setShowTransfer]=useState(false);
  const [inventory, setInventory] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [tab, setTab] = useState("inward");
  const [showModal, setShowModal] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [branches, setBranches] = useState([]);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 10;

  const today = () => new Date().toISOString().split("T")[0];

  const getEmptyRow = () =>
    tab === "inward"
      ? {
          date: today(),
          poId: "",
          item: "",
          qty: "",
          unit: "Kg",
          quality: "pass",
          notes: "",
          branch_id: "",
          vendor: "", // 🔥 ADD THIS
          target_branch_id: "", // 🔥 ADD THIS (safe)
        }
      : {
          date: today(),
          item: "",
          qty: "",
          unit: "Kg",
          purpose: "",
          branch_id: "",
          target_branch_id: "", // 🔥 ADD THIS
        };

  const [rows, setRows] = useState([getEmptyRow()]);

  // ================= FETCH =================
  useEffect(() => {
    fetchInventory();
    fetchPOs();
    fetchLedger();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await fetch("/api/branches", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setBranches(data.data);
      } else {
        console.log("Branch fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Branch error:", err);
    }
  };

  const server=useServerPagination({endpoint:'/inventory/ledger',onData:setLedger,search,page,pageSize:rowsPerPage,onPageChange:setPage,filters:{type:tab,from_date:fromDate,to_date:toDate}});
  const fetchLedger = async () => {server.reload();};

  const fetchInventory = async () => {
    const res = await fetch("/api/inventory/items", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setInventory(data.data);
  };

  const fetchPOs = async () => {
    // ✅ FIXED ENDPOINT
    const res = await fetch("/api/inventory/po", {
      credentials: "include",
    });
    const data = await res.json();
    if (data.status === "success") setPurchaseOrders(data.data);
  };

  // ================= HELPERS =================
  const getPOItems = (poId) => {
    const po = purchaseOrders.find((p) => p.id === Number(poId));
    return po?.items || [];
  };

  const getItemId = (name, row) => {
    if (row?.inventory_item_id) return Number(row.inventory_item_id);
    const branch = row?.branch_id || user?.branch_id;
    const matches = inventory.filter(i => i.name === name && (!branch || Number(i.branch_id) === Number(branch)));
    if (matches.length !== 1) throw new Error('Select the exact inventory item and department; this name is missing or duplicated.');
    return matches[0].id;
  };

  // ================= ROW =================
  const addRow = () => setRows([...rows, getEmptyRow()]);

  const updateRow = (i, field, val) => {
    const updated = [...rows];
    updated[i][field] = val;
    if (field === 'unit') updated[i].purchase_rate = '';
    if (field === 'inventory_item_id') {
      updated[i].purchase_rate = '';
      updated[i].tax_mode = 'none';
      updated[i].tax_percent = 0;
      const selected = inventory.find(item => Number(item.id) === Number(val));
      updated[i].item = selected?.name || '';
      updated[i].branch_id = selected?.branch_id || '';
      if (selected?.unit && selected.unit !== '-') updated[i].unit = selected.unit;
    }
    if (field === 'poId') updated[i].inventory_item_id = '';

    // 🔥 PO → auto-fill first item
    if (field === "poId") {
      const po = purchaseOrders.find((p) => p.id === Number(val));

      if (po) {
        updated[i].vendor = po.vendors?.join(", ");

        const firstItem = po.items?.[0];
        if (firstItem) {
          updated[i].item = firstItem.name;
          updated[i].unit = firstItem.unit;
          updated[i].qty = firstItem.qty;
        }
      }
    }

    // 🔥 ITEM → update unit
    if (field === "item") {
      const poItems = getPOItems(updated[i].poId);
      const selected = poItems.find((it) => it.name === val);

      if (selected) {
        updated[i].unit = selected.unit;
      }
    }

    setRows(updated);
  };

  const removeRow = (i) => {
    setRows(rows.filter((_, idx) => idx !== i));
  };


  const totalPages = server.pagination.pages;

  const currentPage = server.pagination.page;

  const paginatedLedger = ledger;

  useEffect(() => {
    setPage(1);
  }, [tab, search, fromDate, toDate]);

  const getTypeStyle = (type) => {
    if (type === "inward") {
      return "bg-green-500/20 text-green-400";
    }

    if (type === "outward") {
      return "bg-red-500/20 text-red-400";
    }

    return "bg-amber-400/20 text-amber-300";
  };

  const getQuantityStyle = (type, qty) => {
    if (type === "inward") return "text-green-400";
    if (type === "outward") return "text-red-400";

    return Number(qty) >= 0 ? "text-emerald-400" : "text-red-400";
  };

  const getQuantityPrefix = (type, qty) => {
    if (type === "inward") return "+";
    if (type === "outward") return "-";

    return Number(qty) > 0 ? "+" : "";
  };

  // ================= SAVE =================
  const saveEntries = async () => {
    try {
      for (let r of rows) {
        if (!r.item || !r.qty) {
          alert("Item and quantity required");
          return;
        }
      }

      // ================= INWARD =================
      if (tab === "inward") {
        const payload = {
          entries: rows.map((r) => ({
            ...r,
            item_id: getItemId(r.item, r),
            item_name: r.item, // 👈 ADD (for auto-create)
            qty: Number(r.qty),
            unit: r.unit,

            po_id: r.poId || null,

            // 👇 IMPORTANT
            source_type: r.poId ? "po" : "direct",

            // 👇 IMPORTANT
            reference: r.poId ? `PO-${r.poId}` : r.notes || "manual entry",

            branch_id: r.branch_id || null,
          })),
        };

        const res = await fetch("/api/inventory/inward", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.status !== "success") {
          alert(data.message);
          return;
        }
      }

      // ================= OUTWARD =================
      if (tab === "outward") {
        const payload = {
          method: "FIFO",
          entries: rows.map((r) => ({
            item_id: getItemId(r.item, r),
            unit: r.unit,
            qty: Number(r.qty),

            reference: r.purpose || "consumed",

            branch_id: r.branch_id || null,

            // 👇 ADD THIS
            target_branch_id: r.target_branch_id || null,
          })),
        };

        const res = await fetch("/api/inventory/outward", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.status !== "success") {
          alert(data.message);
          return;
        }
      }

      alert("Saved successfully ✅");
      setShowModal(false);
      setRows([getEmptyRow()]);
      fetchInventory();
      fetchLedger();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* HEADER */}
        <div className="flex justify-between">
          <h1 className="text-2xl font-serif">
            {tab === "adjustment"
              ? "Inventory Edit History"
              : "Material Ledger"}
          </h1>
          {(user?.role === 'super_admin' || permissions.includes('inventory.movement')) && <PermissionButton permissionModule="inventory" onClick={() => setShowTransfer(true)} className="px-4 py-2 rounded-xl border border-amber-400/40 text-amber-400">Department transfer</PermissionButton>}

          {tab !== "adjustment" && (
            <PermissionButton permissionModule="inventory"
              onClick={() => {
                setRows([getEmptyRow()]);
                setShowModal(true);
              }}
              className="px-5 py-2 rounded-xl bg-amber-400 text-black"
            >
              + Add {tab === "inward" ? "Inward" : "Outward"}
            </PermissionButton>
          )}
        </div>

        {/* TABS */}
        <div className="flex gap-2 bg-black/40 p-1 rounded-xl w-fit">
          <PermissionButton permissionModule="inventory"
            onClick={() => setTab("inward")}
            className={`px-5 py-2 rounded-lg ${
              tab === "inward" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Inward
          </PermissionButton>

          <PermissionButton permissionModule="inventory"
            onClick={() => setTab("outward")}
            className={`px-5 py-2 rounded-lg ${
              tab === "outward" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Outward
          </PermissionButton>

          <PermissionButton permissionModule="inventory"
            onClick={() => setTab("adjustment")}
            className={`px-5 py-2 rounded-lg ${
              tab === "adjustment" ? "bg-amber-400 text-black" : "text-gray-400"
            }`}
          >
            Edit History
          </PermissionButton>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item, editor, code, branch..."
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2 md:col-span-2"
          />

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <PermissionButton permissionModule="inventory"
            onClick={() => {
              setSearch("");
              setFromDate("");
              setToDate("");
            }}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 md:col-start-4"
          >
            Clear Filters
          </PermissionButton>
        </div>

        {/* ================= LEDGER TABLE ================= */}
        {tab==='outward'&&<HandoverOutwardActivity search={search} fromDate={fromDate} toDate={toDate}/>}
        <div className="bg-black/40 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <><ServerPageStatus pagination={server.pagination}/><table className="w-full text-sm">
              <thead className="bg-black/60 text-gray-400">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">From Department</th>
                  <th className="p-3 text-left">To Department</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-left">Edited By</th>
                  <th className="p-3 text-left">Employee Code</th>
                  <th className="p-3 text-left">Reason / Details</th>
                  <th className="p-3 text-left">Branch</th>
                </tr>
              </thead>

              <tbody>
                {paginatedLedger.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="p-6 text-center text-gray-500">
                      No{" "}
                      {tab === "adjustment"
                        ? "inventory edit history"
                        : "ledger entries"}{" "}
                      found.
                    </td>
                  </tr>
                ) : (
                  paginatedLedger.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-white/5 hover:bg-white/5"
                    >
                      <td className="p-3 whitespace-nowrap text-gray-400">
                        {new Date(row.date).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>

                      <td className="p-3 font-medium">{row.item}</td>
                      <td className="p-3">{row.source_department || '—'}</td>
                      <td className="p-3">{row.destination_department || '—'}</td>

                      <td className="p-3">
                        <span
                          className={`rounded px-2 py-1 text-xs ${getTypeStyle(
                            row.type,
                          )}`}
                        >
                          {row.type === "adjustment" ? "Edited" : row.type}
                        </span>
                      </td>

                      <td
                        className={`p-3 text-center font-medium ${getQuantityStyle(
                          row.type,
                          row.qty,
                        )}`}
                      >
                        {getQuantityPrefix(row.type, row.qty)} {row.qty}{" "}
                        {row.unit || ""}
                      </td>

                      <td className="p-3">{row.edited_by_name || "System"}</td>

                      <td className="p-3">
                        {row.edited_by_employee_id || "-"}
                      </td>

                      <td className="max-w-md p-3 text-xs text-gray-400">
                        {row.notes || row.reference || "—"}
                      </td>

                      <td className="p-3 text-gray-400">{row.branch || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table></>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-b-2xl border-x border-b border-white/10 bg-black/20 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
          <p className="text-gray-400">
            Showing{" "}
            {server.pagination.total === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}
            {" - "}
            {Math.min(currentPage * rowsPerPage, server.pagination.total)} of{" "}
            {server.pagination.total} entries
          </p>

          <div className="flex items-center gap-2">
            <PermissionButton permissionModule="inventory"
              onClick={() => setPage((previous) => Math.max(1, previous - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </PermissionButton>

            <span className="px-2 text-gray-300">
              Page {currentPage} of {totalPages}
            </span>

            <PermissionButton permissionModule="inventory"
              onClick={() =>
                setPage((previous) => Math.min(totalPages, previous + 1))
              }
              disabled={currentPage === totalPages}
              className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </PermissionButton>
          </div>
        </div>

        {/* MODAL */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="w-full max-w-7xl bg-[#0b0b0f] p-8 rounded-2xl border border-white/10">
              <div className="flex justify-between mb-4">
                <h2 className="font-serif text-xl">
                  Add {tab === "inward" ? "Inward" : "Outward"}
                </h2>
              </div>

              {rows.map((row, i) => (
                <div key={i} className="grid grid-cols-8 gap-3 mb-3">
                  {tab==='inward'&&!row.poId&&<InwardPriceDefault row={row} index={i} setRows={setRows}/>}
                  {tab === 'inward' && !row.poId && <StockReceiptFields value={{...row,received_date:row.received_date||row.date}} onChange={next=>setRows(previous=>previous.map((r,index)=>index===i?next:r))} />}
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(i, "date", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  />

                  {tab === "inward" && (
                    <>
                      <select
                        value={row.poId}
                        onChange={(e) => updateRow(i, "poId", e.target.value)}
                        className="bg-black/40 p-2 rounded"
                      >
                        <option value="">Select PO</option>
                        {purchaseOrders.map((po) => (
                          <option key={po.id} value={po.id}>
                            {po.po_number} ({po.vendors.join(", ")})
                          </option>
                        ))}
                      </select>

                      <input
                        value={row.vendor}
                        readOnly
                        placeholder="Vendor auto-filled"
                        className="bg-black/20 p-2 rounded text-gray-400"
                      />
                    </>
                  )}

                  {tab === "inward" && row.poId ? (
                    // ✅ PO SELECTED → SHOW PO ITEMS
                    <select
                      value={row.item}
                      onChange={(e) => updateRow(i, "item", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Select PO Item</option>
                      {getPOItems(row.poId).map((it, idx) => (
                        <option key={idx} value={it.name}>
                          {it.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // ✅ NO PO → SELECT EXISTING ITEMS
                    <select
                      value={row.inventory_item_id || ''}
                      onChange={(e) => updateRow(i, "inventory_item_id", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Select Item / Department</option>
                      {inventory.map((it) => (
                        <option key={it.id} value={it.id}>
                          {it.name} · {it.department || 'No department'} · {it.branch}
                        </option>
                      ))}
                    </select>
                  )}

                  <input
                    type="number"
                    placeholder="Qty"
                    value={row.qty}
                    onChange={(e) => updateRow(i, "qty", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  />

                  <select
                    value={row.unit}
                    onChange={(e) => updateRow(i, "unit", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  >
                    {UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                  </select>

                  <select
                    value={row.branch_id}
                    onChange={(e) => updateRow(i, "branch_id", e.target.value)}
                    className="bg-black/40 p-2 rounded"
                  >
                    <option value="">Select Branch</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>

                  {tab === "outward" && (
                    <select
                      value={row.target_branch_id}
                      onChange={(e) =>
                        updateRow(i, "target_branch_id", e.target.value)
                      }
                      className="bg-black/40 p-2 rounded"
                    >
                      <option value="">Transfer To (optional)</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {tab === "inward" ? (
                    <>
                      <select
                        value={row.quality}
                        onChange={(e) =>
                          updateRow(i, "quality", e.target.value)
                        }
                        className="bg-black/40 p-2 rounded"
                      >
                        <option value="pass">Quality OK</option>
                        <option value="conditional">Conditional</option>
                        <option value="fail">Rejected</option>
                      </select>

                      <input
                        placeholder="QC Notes"
                        value={row.notes}
                        onChange={(e) => updateRow(i, "notes", e.target.value)}
                        className="bg-black/40 p-2 rounded"
                      />

                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) =>
                          updateRow(i, "video", e.target.files[0])
                        }
                        className="bg-black/40 p-2 rounded"
                      />
                    </>
                  ) : (
                    <input
                      placeholder="Purpose"
                      value={row.purpose}
                      onChange={(e) => updateRow(i, "purpose", e.target.value)}
                      className="bg-black/40 p-2 rounded"
                    />
                  )}

                  <PermissionButton permissionAction="form" permissionModule="inventory" onClick={() => removeRow(i)} className="text-red-400">
                    Remove
                  </PermissionButton>
                </div>
              ))}

              <div className="flex justify-between mt-6">
                <PermissionButton permissionModule="inventory" onClick={addRow}>+ Add Row</PermissionButton>
                <div className="flex gap-3">
                  <PermissionButton permissionModule="inventory" onClick={() => setShowModal(false)}>Cancel</PermissionButton>
                  <PermissionButton permissionModule="inventory"
                    onClick={saveEntries}
                    className="bg-amber-400 text-black px-4 py-2 rounded"
                  >
                    Save
                  </PermissionButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {showTransfer && <DepartmentTransferModal inventory={inventory} onClose={() => setShowTransfer(false)} onSaved={() => { fetchInventory(); fetchLedger(); }} />}
    </DashboardLayout>
  );
}
